<script setup lang="ts">
import { ref, computed, watch, onBeforeUnmount } from "vue";
import Dialog from "primevue/dialog";
import Button from "primevue/button";
import Message from "primevue/message";
import InputNumber from "primevue/inputnumber";
import SelectEntity from "../form/SelectEntity.vue";
import { useTicketService } from "src/services/ticket-service";
import { useNotification } from "src/composables/useNotification";
import { CreateActionLabourDTOType, CreateAssignmentDTOType, FetchAssignmentResponseStatus } from "src/api/models";
import OperationDetails from "./OperationDetails.vue";
import { useConfirm } from "src/composables/useConfirm";

const props = defineProps<{
  visible: boolean;
  ticketId?: string;
  existingAssignment?: Record<string, any> | null;
  mode?: 'add' | 'edit';
  ticketAssignee?: { id: string; name: string; function?: string; price_per_hour?: number } | null;
  actionName?: string;
}>();

const emit = defineEmits<{
  "update:visible": [value: boolean];
  "operation-added": [];
  "operation-updated": [];
  "update:status": [assignmentId: string, status: FetchAssignmentResponseStatus];
}>();

const { addAssignmentToTicket, addLaborToAssignment, closeAssignment, openAssignment, getAssignment, updateActionLabourTotalHours, removeActionLabour } = useTicketService();
const { showSuccess, showError } = useNotification();
const { confirm } = useConfirm();

// Mode: 'work' (default) or 'offer'
const addMode = ref<'work' | 'offer'>('work');

const selectedAction = ref<Record<string, any> | null>(null);
const isAssignmentOpen = ref(true);
const shouldCloseAssignment = ref(false);
const currentAssignment = ref<Record<string, any> | null>(null);
const isLoading = ref(false);

// Offer mode state
const offerMechanic = ref<Record<string, any> | null>(null);
const offerHours = ref(1);

interface LaborEntry {
  id: number;
  assignee: Record<string, any> | null;
  hours: number;
  type: CreateActionLabourDTOType;
  isExisting?: boolean;
  action_labour_id?: string;
}

const laborEntries = ref<LaborEntry[]>([]);
const existingLaborEntries = ref<LaborEntry[]>([]);
const entryIdCounter = ref(1);
const isSubmitting = ref(false);
const errorMessage = ref("");

const pendingHourUpdates = ref<Map<string, number>>(new Map());
const updatingLaborEntries = ref<Set<string>>(new Set());

const currentMode = computed(() => {
  return props.mode || (props.existingAssignment ? 'edit' : 'add');
});

const isEditMode = computed(() => {
  return currentMode.value === 'edit';
});

const isAddingNew = computed(() => {
  return currentMode.value === 'add';
});

const dialogTitle = computed(() => {
  if (isEditMode.value) return "Edit Assignment";
  if (addMode.value === 'offer') return "Add to Offer";
  return "Add Work Assignment";
});

const isFormValid = computed(() => {
  if (isEditMode.value) {
    if (props.existingAssignment?.status === FetchAssignmentResponseStatus.open && !isAssignmentOpen.value) {
      return true;
    }
    return true;
  } else {
    if (!selectedAction.value?.id || !props.ticketId) return false;

    if (addMode.value === 'offer') {
      return !!offerMechanic.value?.id && offerHours.value > 0;
    }
    return true;
  }
});

const messageMap = {
  success: {
    labor: (statusChanged: boolean, newStatus?: FetchAssignmentResponseStatus) => {
      if (statusChanged && newStatus === FetchAssignmentResponseStatus.closed) {
        return "Labor hours added and operation closed";
      } else if (statusChanged && newStatus === FetchAssignmentResponseStatus.open) {
        return "Labor hours added and operation opened";
      }
      return "Labor hours added successfully";
    },
    add: "Assignment added successfully",
    statusChange: (newStatus: string) => `Assignment ${newStatus} successfully`,
    edit: "Assignment updated successfully"
  },
  error: {
    labor: "Failed to add labor hours",
    add: "Failed to add assignment",
    edit: "Failed to update assignment",
    tryAgain: (action: string) => `Failed to ${action}. Please try again.`
  }
};

async function fetchLatestAssignment() {
  if (!props.existingAssignment?.id) {
    console.error("No assignment ID available to fetch", props.existingAssignment);
    return;
  }

  try {
    isLoading.value = true;
    const assignment = await getAssignment(props.existingAssignment.id);

    if (assignment) {
      currentAssignment.value = assignment;
      updateFromAssignment(assignment);
    } else {
      console.warn("No assignment data returned from API");
    }
  } catch (error) {
    console.error("Error fetching assignment:", error);
    showError({ message: "Failed to fetch latest assignment data" });
  } finally {
    isLoading.value = false;
  }
}

function updateFromAssignment(assignment: Record<string, any>) {
  if (!assignment) return;

  selectedAction.value = assignment.action || null;
  isAssignmentOpen.value = assignment.status === FetchAssignmentResponseStatus.open;

  existingLaborEntries.value = [];
  laborEntries.value = [];
  pendingHourUpdates.value.clear();

  const entries = assignment.labor_entries || assignment.action_labours || [];

  if (entries.length > 0) {
    existingLaborEntries.value = entries.map((entry: any, index: number) => {
      return {
        id: index + 1,
        assignee: entry.assignee || null,
        hours: entry.labor_hours || entry.total_hours || 0,
        type: entry.labor_type || entry.type || CreateActionLabourDTOType.variable,
        isExisting: true,
        action_labour_id: entry.id
      };
    });
    entryIdCounter.value = existingLaborEntries.value.length + 1;
  }

}

function handleModelChange(value: boolean) {
  if (!value && pendingHourUpdates.value.size > 0) {
    emit("update:visible", false);
    resetForm();
  } else {
    emit("update:visible", value);

    if (value && isEditMode.value && props.existingAssignment?.id) {
      fetchLatestAssignment();
    } else if (!value) {
      resetForm();
    }
  }
}

function resetForm() {
  pendingHourUpdates.value.clear();
  updatingLaborEntries.value.clear();

  selectedAction.value = null;
  laborEntries.value = [];
  existingLaborEntries.value = [];
  entryIdCounter.value = 1;
  errorMessage.value = "";
  isSubmitting.value = false;
  isAssignmentOpen.value = true;
  shouldCloseAssignment.value = false;
  currentAssignment.value = null;
  addMode.value = 'work';
  offerMechanic.value = null;
  offerHours.value = 1;
}

function addLaborEntry() {
  laborEntries.value.push({
    id: entryIdCounter.value++,
    assignee: props.ticketAssignee || null,
    hours: 1,
    type: CreateActionLabourDTOType.variable,
  });
}

function removeLaborEntry(entryId: number) {
  laborEntries.value = laborEntries.value.filter((entry) => entry.id !== entryId);
}

function handleStatusToggle(isOpen: boolean) {
  isAssignmentOpen.value = isOpen;
  shouldCloseAssignment.value = !isOpen;
}

async function handleSubmit() {
  errorMessage.value = "";
  isSubmitting.value = true;

  try {
    if (pendingHourUpdates.value.size > 0) {
      await applyPendingHourUpdates();
    }

    const validEntries = getValidLaborEntries();
    const result = await performOperation(validEntries);

    if (result.success) {
      showSuccess({ message: getSuccessMessage(result) });
      resetForm();
      emit("update:visible", false);
    } else {
      errorMessage.value = getErrorMessage();
    }
  } catch (error) {
    console.error("Error:", error);
    errorMessage.value = messageMap.error.tryAgain(getCurrentOperation());
  } finally {
    isSubmitting.value = false;
  }
}

async function applyPendingHourUpdates(): Promise<void> {
  const updates = Array.from(pendingHourUpdates.value.entries());

  for (const [actionLabourId, hours] of updates) {
    try {
      updatingLaborEntries.value.add(actionLabourId);
      await updateActionLabourTotalHours(actionLabourId, hours);
      updatingLaborEntries.value.delete(actionLabourId);
    } catch (error) {
      console.error(`Error updating hours for entry ${actionLabourId}:`, error);
      updatingLaborEntries.value.delete(actionLabourId);
    }
  }

  pendingHourUpdates.value.clear();
}

function getValidLaborEntries() {
  if (addMode.value === 'offer' && isAddingNew.value) {
    if (offerMechanic.value?.id && offerHours.value > 0) {
      return [{
        assignee_id: offerMechanic.value.id,
        labor_hours: offerHours.value,
        labor_type: CreateActionLabourDTOType.fixed,
      }];
    }
    return [];
  }

  return laborEntries.value
    .filter((entry) => entry.assignee?.id && entry.hours > 0)
    .map((entry) => ({
      assignee_id: entry.assignee?.id,
      labor_hours: entry.hours,
      labor_type: entry.type,
    }));
}

async function performOperation(mappedEntries: Array<{assignee_id: string, labor_hours: number, labor_type: CreateActionLabourDTOType}>) {
  const result = {
    success: false,
    statusChanged: false,
    operation: getCurrentOperation(),
    newStatus: undefined as FetchAssignmentResponseStatus | undefined
  };

  if (shouldAddLaborToExisting(mappedEntries)) {
    result.success = await addLaborToExisting(mappedEntries);
  }
  else if (shouldCreateNewAssignment()) {
    result.success = await createNewAssignment(mappedEntries);
  }
  else if (isEditMode.value) {
    result.success = true;
    emit("operation-updated");
  }
  else {
    errorMessage.value = "Missing required information";
    isSubmitting.value = false;
    return result;
  }

  if (shouldChangeStatus(result.success)) {
    result.statusChanged = await changeAssignmentStatus();
    if (result.statusChanged) {
      result.newStatus = isAssignmentOpen.value ? FetchAssignmentResponseStatus.open : FetchAssignmentResponseStatus.closed;
    }
  }

  return result;
}

function shouldAddLaborToExisting(entries: any[]): boolean {
  return isEditMode.value &&
         !!props.existingAssignment?.id &&
         entries.length > 0;
}

async function addLaborToExisting(entries: any[]): Promise<boolean> {
  if (!props.existingAssignment?.id) return false;

  const success = await addLaborToAssignment(props.existingAssignment.id, entries);
  if (success) {
    emit("operation-updated");
  }
  return success;
}

function shouldCreateNewAssignment(): boolean {
  return isAddingNew.value &&
         !!selectedAction.value?.id &&
         !!props.ticketId;
}

async function createNewAssignment(entries: any[]): Promise<boolean> {
  if (!props.ticketId || !selectedAction.value?.id) return false;

  const assignmentType = addMode.value === 'offer'
    ? CreateAssignmentDTOType.fixed
    : CreateAssignmentDTOType.variable;

  const addResult = await addAssignmentToTicket(props.ticketId, {
    action_id: selectedAction.value.id,
    laborEntries: entries.length > 0 ? entries : undefined,
    type: assignmentType,
  });

  if (addResult) {
    emit("operation-added");
  }
  return !!addResult;
}

function shouldChangeStatus(operationSucceeded: boolean): boolean {
  return operationSucceeded &&
         isEditMode.value &&
         !!props.existingAssignment?.id &&
         ((shouldCloseAssignment.value && props.existingAssignment.status === FetchAssignmentResponseStatus.open) ||
          (isAssignmentOpen.value && props.existingAssignment.status === FetchAssignmentResponseStatus.closed));
}

async function changeAssignmentStatus(): Promise<boolean> {
  if (!props.existingAssignment?.id) return false;

  try {
    let statusChanged = false;
    let newStatus: FetchAssignmentResponseStatus;

    if (shouldCloseAssignment.value && props.existingAssignment.status === FetchAssignmentResponseStatus.open) {
      statusChanged = await closeAssignment(props.existingAssignment.id);
      newStatus = FetchAssignmentResponseStatus.closed;
    } else if (isAssignmentOpen.value && props.existingAssignment.status === FetchAssignmentResponseStatus.closed) {
      statusChanged = await openAssignment(props.existingAssignment.id);
      newStatus = FetchAssignmentResponseStatus.open;
    }

    if (statusChanged) {
      emit("update:status", props.existingAssignment.id, newStatus!);
      emit("operation-updated");
    }
    return statusChanged;
  } catch (error) {
    const action = shouldCloseAssignment.value ? 'close' : 'open';
    console.error(`Error ${action}ing assignment:`, error);
    showError({ message: `Added labor hours but failed to ${action} operation` });
    return false;
  }
}

function getCurrentOperation(): string {
  if (isEditMode.value) {
    const hasLaborEntries = laborEntries.value.some(e => e.assignee?.id && e.hours > 0);
    const hasPendingUpdates = pendingHourUpdates.value.size > 0;
    return (hasLaborEntries || hasPendingUpdates) ? 'labor' : 'edit';
  }
  return 'add';
}

function getSuccessMessage(result: { statusChanged: boolean, operation: string, newStatus?: FetchAssignmentResponseStatus }): string {
  if (isEditMode.value && (laborEntries.value.some(e => e.assignee?.id && e.hours > 0) || pendingHourUpdates.value.size > 0)) {
    return messageMap.success.labor(result.statusChanged, result.newStatus);
  }

  if (isAddingNew.value) return messageMap.success.add;
  if (result.statusChanged && result.newStatus) return messageMap.success.statusChange(result.newStatus);
  return messageMap.success.edit;
}

function getErrorMessage(): string {
  if (isAddingNew.value) return messageMap.error.add;
  return messageMap.error.edit;
}

function handleUpdateLaborHours(actionLabourId: string, hours: number) {
  pendingHourUpdates.value.set(actionLabourId, hours);
  updatingLaborEntries.value.add(actionLabourId);
}

async function handleRemoveExistingLabor(entryId: string, event: Event) {
  if (!entryId) return;

  const { onAccept } = confirm(event);

  onAccept(async () => {
    try {
      const success = await removeActionLabour(entryId);

      if (success) {
        existingLaborEntries.value = existingLaborEntries.value.filter(entry =>
          entry.action_labour_id !== entryId
        );
        showSuccess({ message: "Labor entry removed successfully" });
      } else {
        showError({ message: "Failed to remove labor entry" });
      }
    } catch (error) {
      console.error("Error removing labor entry:", error);
      showError({ message: "Error removing labor entry" });
    }
  });
}

watch(
  () => props.visible,
  (value) => {
    if (!value) {
      resetForm();
    } else if (isEditMode.value && props.existingAssignment?.id) {
      fetchLatestAssignment();
    } else if (isAddingNew.value && props.ticketAssignee) {
      // Preselect the ticket's assignee for offer mode
      offerMechanic.value = props.ticketAssignee;
    }
  }
);

watch(
  () => props.existingAssignment,
  (value) => {
    if (value && props.visible) {
      fetchLatestAssignment();
    }
  },
  { immediate: true, deep: true }
);

watch(
  () => props.mode,
  (newMode) => {
    errorMessage.value = "";

    if (props.visible && newMode === 'edit' && props.existingAssignment?.id) {
      fetchLatestAssignment();
    }
  }
);

onBeforeUnmount(() => {
  pendingHourUpdates.value.clear();
  updatingLaborEntries.value.clear();
});
</script>

<template>
  <Dialog
    :visible="visible"
    :modal="true"
    :header="dialogTitle"
    :dismissableMask="true"
    class="operation-dialog"
    :style="{ width: '700px' }"
    @update:visible="handleModelChange"
  >
    <div class="flex flex-col gap-4 p-2">
      <!-- Mode Selector (Add Mode Only) -->
      <div v-if="isAddingNew" class="flex gap-2">
        <Button
          :severity="addMode === 'work' ? 'primary' : 'secondary'"
          :outlined="addMode !== 'work'"
          label="Work Assignment"
          icon="pi pi-wrench"
          @click="addMode = 'work'"
          class="flex-1"
        />
        <Button
          :severity="addMode === 'offer' ? 'primary' : 'secondary'"
          :outlined="addMode !== 'offer'"
          label="For Offer/Quote"
          icon="pi pi-file"
          @click="addMode = 'offer'"
          class="flex-1"
        />
      </div>

      <!-- Action Selector (Add Mode) -->
      <div v-if="isAddingNew" class="flex flex-col gap-1">
        <label class="font-medium text-gray-800 dark:text-gray-100 text-sm">Select Action</label>
        <SelectEntity
          entity="abstract-actions"
          v-model="selectedAction"
          :entity-label="['name', 'type']"
          entity-value="id"
          placeholder="Search actions by name or type..."
          :return-entity="true"
          class="w-full"
        />
      </div>

      <!-- OFFER MODE: Simple mechanic + hours -->
      <div v-if="isAddingNew && addMode === 'offer'" class="border border-blue-200 dark:border-blue-800 rounded p-4 bg-blue-50 dark:bg-blue-900/20">
        <div class="text-sm text-blue-700 dark:text-blue-300 mb-3">
          Select a mechanic and hours for pricing. The hourly rate will be used to calculate the offer price.
        </div>

        <div class="flex flex-col gap-3">
          <div class="flex flex-col gap-1">
            <label class="font-medium text-gray-800 dark:text-gray-100 text-sm">Mechanic</label>
            <SelectEntity
              entity="assignees"
              v-model="offerMechanic"
              :entity-label="['name']"
              entity-value="id"
              placeholder="Select mechanic for pricing..."
              :return-entity="true"
              class="w-full"
            />
          </div>

          <div class="flex flex-col gap-1">
            <label class="font-medium text-gray-800 dark:text-gray-100 text-sm">Hours</label>
            <InputNumber
              v-model="offerHours"
              :min="0.5"
              :step="0.5"
              :minFractionDigits="1"
              :maxFractionDigits="2"
              suffix=" h"
              class="w-full"
            />
          </div>
        </div>
      </div>

      <!-- WORK MODE: Full labor entries -->
      <div v-if="isAddingNew && addMode === 'work'" class="border border-gray-200 dark:border-gray-700 rounded p-3 bg-white dark:bg-gray-900">
        <div class="flex items-center justify-between mb-3">
          <h3 class="text-base font-medium text-gray-800 dark:text-gray-100 m-0">Assign Mechanics</h3>
          <Button
            label="Add Mechanic"
            icon="pi pi-plus"
            size="small"
            severity="secondary"
            outlined
            @click="addLaborEntry"
          />
        </div>

        <div v-if="laborEntries.length > 0">
          <div class="grid grid-cols-12 gap-2 text-xs font-medium text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-700 pb-1 mb-2">
            <div class="col-span-5">Mechanic</div>
            <div class="col-span-3">Type</div>
            <div class="col-span-3">Hours</div>
            <div class="col-span-1"></div>
          </div>

          <div v-for="entry in laborEntries" :key="entry.id" class="grid grid-cols-12 gap-2 mb-2 items-center">
            <div class="col-span-5">
              <SelectEntity
                entity="assignees"
                v-model="entry.assignee"
                :entity-label="['name']"
                entity-value="id"
                placeholder="Select mechanic"
                :return-entity="true"
                class="w-full"
                :clearable="false"
              />
            </div>
            <div class="col-span-3">
              <select
                v-model="entry.type"
                class="w-full p-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-800 rounded text-sm"
              >
                <option v-for="laborType in CreateActionLabourDTOType" :key="laborType" :value="laborType">
                  {{ laborType }}
                </option>
              </select>
            </div>
            <div class="col-span-3">
              <InputNumber
                v-model="entry.hours"
                :min="0.5"
                :step="0.5"
                :minFractionDigits="1"
                suffix=" h"
                class="w-full"
                inputClass="text-right"
              />
            </div>
            <div class="col-span-1 flex justify-center">
              <Button
                icon="pi pi-times"
                text
                rounded
                size="small"
                severity="danger"
                @click="removeLaborEntry(entry.id)"
              />
            </div>
          </div>
        </div>

        <div v-else class="text-center py-4 text-gray-500 dark:text-gray-400">
          Click "Add Mechanic" to assign work
        </div>
      </div>

      <!-- Edit Mode: Operation Details -->
      <OperationDetails
        v-if="isEditMode && (currentAssignment || existingAssignment)"
        :assignment="currentAssignment ?? existingAssignment!"
        :isOpen="isAssignmentOpen"
        :action-name="actionName"
        @update:isOpen="handleStatusToggle"
      />

      <!-- Error Message -->
      <Message severity="error" v-if="errorMessage" class="p-2 text-sm">
        {{ errorMessage }}
      </Message>

      <!-- Edit Mode: Labor Section -->
      <div v-if="isEditMode" class="border border-gray-200 dark:border-gray-700 rounded p-3 bg-white dark:bg-gray-900">
        <div class="flex items-center justify-between mb-2">
          <h3 class="text-base font-medium text-gray-800 dark:text-gray-100 m-0">Labor Entries</h3>
          <Button
            label="Add Labor"
            icon="pi pi-plus"
            size="small"
            severity="secondary"
            outlined
            @click="addLaborEntry"
          />
        </div>

        <!-- Existing Entries Section -->
        <div v-if="existingLaborEntries.length > 0" class="mb-3">
          <div class="text-xs text-gray-500 dark:text-gray-400 mb-1 font-medium uppercase">Existing Entries</div>
          <div class="grid grid-cols-12 gap-2 text-xs font-medium text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-700 pb-1 mb-1">
            <div class="col-span-4">Mechanic</div>
            <div class="col-span-3">Type</div>
            <div class="col-span-4">Hours</div>
            <div class="col-span-1"></div>
          </div>

          <div v-for="entry in existingLaborEntries" :key="entry.id" class="grid grid-cols-12 gap-2 mb-2 items-center text-sm">
            <div class="col-span-4">
              <div class="flex items-center gap-1">
                <i class="pi pi-user text-gray-400 text-xs"></i>
                <span class="truncate">{{ entry.assignee?.name || 'Unknown' }}</span>
              </div>
            </div>
            <div class="col-span-3">
              <span class="capitalize">{{ entry.type }}</span>
            </div>
            <div class="col-span-4">
              <div class="flex items-center gap-1">
                <input
                  type="number"
                  min="0.5"
                  step="0.5"
                  class="w-20 p-1 border border-gray-300 dark:border-gray-600 dark:bg-gray-800 rounded text-right"
                  :class="{ 'bg-yellow-50 dark:bg-yellow-900/20': updatingLaborEntries.has(entry.action_labour_id || '') }"
                  v-model.number="entry.hours"
                  @input="entry.action_labour_id && handleUpdateLaborHours(entry.action_labour_id, entry.hours)"
                />
                <span class="text-gray-500 dark:text-gray-400">h</span>
              </div>
            </div>
            <div class="col-span-1 flex justify-center">
              <i v-if="updatingLaborEntries.has(entry.action_labour_id || '')" class="pi pi-spin pi-spinner text-blue-500"></i>
              <button
                v-else-if="entry.action_labour_id"
                @click="$event => handleRemoveExistingLabor(entry.action_labour_id || '', $event)"
                class="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                title="Remove labor entry"
              >
                <i class="pi pi-trash text-red-500 dark:text-red-400 text-sm" />
              </button>
            </div>
          </div>
        </div>

        <!-- New Entries Section -->
        <div v-if="laborEntries.length > 0">
          <div class="text-xs text-gray-500 dark:text-gray-400 mb-1 font-medium uppercase">New Entries</div>
          <div class="grid grid-cols-12 gap-2 text-xs font-medium text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-700 pb-1 mb-1">
            <div class="col-span-4">Mechanic</div>
            <div class="col-span-3">Type</div>
            <div class="col-span-4">Hours</div>
            <div class="col-span-1"></div>
          </div>

          <div v-for="entry in laborEntries" :key="entry.id" class="grid grid-cols-12 gap-2 mb-2 items-center text-sm">
            <div class="col-span-4">
              <SelectEntity
                entity="assignees"
                v-model="entry.assignee"
                :entity-label="['name']"
                entity-value="id"
                placeholder="Select mechanic"
                :return-entity="true"
                class="w-full"
                :clearable="false"
              />
            </div>
            <div class="col-span-3">
              <select
                v-model="entry.type"
                class="w-full p-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-800 rounded text-sm"
              >
                <option v-for="laborType in CreateActionLabourDTOType" :key="laborType" :value="laborType">
                  {{ laborType }}
                </option>
              </select>
            </div>
            <div class="col-span-4">
              <div class="flex items-center gap-1">
                <input
                  type="number"
                  min="0.5"
                  step="0.5"
                  class="w-20 p-1 border border-gray-300 dark:border-gray-600 dark:bg-gray-800 rounded text-right"
                  v-model.number="entry.hours"
                />
                <span class="text-gray-500 dark:text-gray-400">h</span>
              </div>
            </div>
            <div class="col-span-1 flex justify-center">
              <Button
                icon="pi pi-times"
                text
                rounded
                size="small"
                severity="danger"
                @click="removeLaborEntry(entry.id)"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Status change messages -->
      <Message v-if="isEditMode && !isAssignmentOpen && existingAssignment?.status === FetchAssignmentResponseStatus.open" severity="info" class="p-2 text-sm">
        This assignment will be closed when you save.
      </Message>
      <Message v-if="isEditMode && isAssignmentOpen && existingAssignment?.status === FetchAssignmentResponseStatus.closed" severity="info" class="p-2 text-sm">
        This assignment will be reopened when you save.
      </Message>

      <!-- Pending updates message -->
      <Message v-if="pendingHourUpdates.size > 0" severity="info" class="p-2 text-sm">
        {{ pendingHourUpdates.size }} pending hour {{ pendingHourUpdates.size === 1 ? 'change' : 'changes' }} will be applied when you save.
      </Message>
    </div>

    <template #footer>
      <div class="flex justify-between items-center w-full">
        <div class="text-sm text-gray-500 dark:text-gray-400" v-if="isSubmitting">
          <i class="pi pi-spin pi-spinner mr-1"></i> Processing...
        </div>
        <div v-else></div>
        <div class="flex gap-2">
          <Button
            label="Cancel"
            icon="pi pi-times"
            @click="emit('update:visible', false)"
            text
            severity="secondary"
            :disabled="isSubmitting"
          />
          <Button
            :label="isEditMode
              ? (pendingHourUpdates.size > 0 ? `Save ${pendingHourUpdates.size} Changes` : 'Save Changes')
              : (addMode === 'offer' ? 'Add to Offer' : 'Add Assignment')"
            :icon="isEditMode ? 'pi pi-save' : 'pi pi-plus'"
            @click="handleSubmit"
            :disabled="!isFormValid || isSubmitting"
            severity="success"
            :loading="isSubmitting"
          />
        </div>
      </div>
    </template>
  </Dialog>
</template>
